import { inject, Injectable, Provider } from '@angular/core';
import { map, Observable } from 'rxjs';
import ProjectLinks from 'src/app/common/project.links';
import {
  CategoryBasicView,
  CharityCommunity,
  CommunityBasic,
  CommunityFocus,
  CommunityPermissions,
  CommunityStatus,
  CreateCharityRequest,
  CreateCommunityRequest,
  CreateCommunityRequestBasic,
  DefaultService,
  EmptyResponse,
  HealthCommunity,
  LocationObject,
  UpdateCharityRequest,
  UpdateCommunityRequest,
  UpdateCommunityRequestBasic,
  UserBasic,
  UserRelationToGroup,
} from 'src/app/swagger-api';
import { InviteToSocialGroupRouteService } from '../search/search-component/invite-to-social-group/social-group-resolver';
import { SocialInitiative } from '../shared/social-initiative/social-initiative.abstract';
import { CommunityFormData } from './create-community.interfaces';
import { ManageCommunityService } from './manage-community.service';

export interface CommunityView {
  entity_id: string;
  title: string;
  type: string;
  focus: CommunityFocus;
  location: LocationObject;
  creator: UserBasic;
  created: Date;
  status: CommunityStatus;
  actor_role: UserRelationToGroup;
  permissions: CommunityPermissions;
  description: string;
  website?: string;
  linked_communities?: Array<CommunityBasic>;
  specialities: Array<CategoryBasicView>;
  diseases: Array<CategoryBasicView>;
  charity_focuses: Array<CategoryBasicView>;
}

@Injectable({ providedIn: 'root' })
export abstract class CommunityStore<
  TCreateRequest extends CreateCommunityRequestBasic,
  TUpdateRequest extends UpdateCommunityRequestBasic,
> {
  protected api: DefaultService = inject(DefaultService);

  public abstract readonly VIEW_ROUTE: string;
  public abstract readonly MANAGE_ROUTE: string;
  public abstract readonly INVITE_ROUTE: string;

  public delete(id: string): Observable<EmptyResponse> {
    return this.api.restSocialCommunitiesDelete(id);
  }

  public abstract get(id: string): Observable<CommunityView>;
  public abstract create(community: TCreateRequest): Observable<CommunityView>;
  public abstract update(community: TUpdateRequest, id: string): Observable<CommunityView>;
}

@Injectable({ providedIn: 'root' })
export class HealthCommunityStore extends CommunityStore<CreateCommunityRequest, UpdateCommunityRequest> {
  public readonly VIEW_ROUTE = ProjectLinks.LINK_COMMUNITY_VIEW;
  public readonly MANAGE_ROUTE = ProjectLinks.LINK_COMMUNITY_MANAGE;
  public readonly INVITE_ROUTE = ProjectLinks.LINK_COMMUNITY_INVITE;

  public get(id: string): Observable<CommunityView> {
    return this.api.restSocialCommunitiesGet(id).pipe(map(CommunityMapper.fromHealthCommunityToView));
  }

  public create(community: CreateCommunityRequest): Observable<CommunityView> {
    return this.api.restSocialCommunitiesPost(community).pipe(map(CommunityMapper.fromHealthCommunityToView));
  }

  public update(community: UpdateCommunityRequest, id: string): Observable<CommunityView> {
    return this.api.restSocialCommunitiesPut(community, id).pipe(map(CommunityMapper.fromHealthCommunityToView));
  }
}

@Injectable()
export class CharityCommunityStore extends CommunityStore<CreateCharityRequest, UpdateCharityRequest> {
  public readonly VIEW_ROUTE = ProjectLinks.LINK_CHARITY_VIEW;
  public readonly MANAGE_ROUTE = ProjectLinks.LINK_CHARITY_MANAGE;
  public readonly INVITE_ROUTE = ProjectLinks.LINK_CHARITY_INVITE;

  public get(id: string): Observable<CommunityView> {
    return this.api.restSocialCharitiesGet(id).pipe(map(CommunityMapper.fromCharityCommunityToView));
  }

  public create(charity: CreateCharityRequest): Observable<CommunityView> {
    return this.api.restSocialCharitiesPost(charity).pipe(map(CommunityMapper.fromCharityCommunityToView));
  }

  public update(charity: UpdateCharityRequest, id: string): Observable<CommunityView> {
    return this.api.restSocialCharitiesPut(charity, id).pipe(map(CommunityMapper.fromCharityCommunityToView));
  }
}

export class CommunityMapper {
  static isCharityEntity(entity: CommunityFormData): boolean {
    return entity.focus === 'CHARITY';
  }

  static isCommunityEntity(entity: CommunityFormData): boolean {
    return entity.focus === 'HEALTH';
  }

  public static toCharityCreateRequest(formData: CommunityFormData): CreateCharityRequest {
    const { title, description, status, viewport, website, type, location, charity_focuses, linked_communities } =
      formData;

    return {
      title,
      description,
      website,
      type,
      location,
      charity_focus_ids: charity_focuses,
      viewport,
      status,
      linked_communities,
    };
  }

  public static toCommunityCreateRequest(formData: CommunityFormData): CreateCommunityRequest {
    const {
      title,
      description,
      status,
      viewport,
      website,
      type,
      location,
      diseases,
      specialities,
      linked_communities,
    } = formData;

    return {
      title,
      description,
      website,
      type,
      location,
      disease_ids: diseases,
      specialty_ids: specialities,
      viewport,
      status,
      linked_communities,
    };
  }

  public static toCharityUpdateRequest(formData: CommunityFormData): UpdateCharityRequest {
    return CommunityMapper.toCharityCreateRequest(formData);
  }

  public static toCommunityUpdateRequest(formData: CommunityFormData): UpdateCommunityRequest {
    return CommunityMapper.toCommunityCreateRequest(formData);
  }

  public static fromCharityCommunityToView(charity: CharityCommunity): CommunityView {
    return {
      entity_id: charity.entity_id,
      title: charity.title,
      type: charity.type,
      focus: charity.focus,
      location: charity.location,
      creator: charity.creator,
      created: charity.created,
      status: charity.status,
      actor_role: charity.actor_role,
      permissions: charity.permissions,
      description: charity.description,
      website: charity.website,
      linked_communities: charity.linked_communities,
      charity_focuses: charity.charity_focuses,
      specialities: [],
      diseases: [],
    };
  }

  public static fromHealthCommunityToView(community: HealthCommunity): CommunityView {
    return {
      entity_id: community.entity_id,
      title: community.title,
      type: community.type,
      focus: community.focus,
      location: community.location,
      creator: community.creator,
      created: community.created,
      status: community.status,
      actor_role: community.actor_role,
      permissions: community.permissions,
      description: community.description,
      website: community.website,
      linked_communities: community.linked_communities,
      diseases: community.diseases,
      specialities: community.specialities,
      charity_focuses: [],
    };
  }
}

const createCommunityServiceFactory =
  <TCreateRequest extends CreateCommunityRequestBasic, TUpdateRequest extends UpdateCommunityRequestBasic>() =>
  () => {
    inject(CommunityStore);
    return new ManageCommunityService<TCreateRequest, TUpdateRequest>();
  };

export const HEALTH_COMMUNITY_PROVIDERS: Provider[] = [
  {
    provide: CommunityStore,
    useClass: HealthCommunityStore,
  },
  {
    provide: SocialInitiative,
    useFactory: createCommunityServiceFactory<CreateCommunityRequest, UpdateCommunityRequest>(),
  },
  InviteToSocialGroupRouteService,
];

export const CHARITY_COMMUNITY_PROVIDERS: Provider[] = [
  {
    provide: CommunityStore,
    useClass: CharityCommunityStore,
  },
  {
    provide: SocialInitiative,
    useFactory: createCommunityServiceFactory<CreateCharityRequest, UpdateCharityRequest>(),
  },
  InviteToSocialGroupRouteService,
];

export const HEALTH_COMMUNITY_ROUTE_PROVIDERS: Provider[] = [
  {
    provide: CommunityStore,
    useClass: HealthCommunityStore,
  },
  {
    provide: ManageCommunityService,
    useFactory: createCommunityServiceFactory<CreateCommunityRequest, UpdateCommunityRequest>(),
  },
];

export const CHARITY_COMMUNITY_ROUTE_PROVIDERS: Provider[] = [
  {
    provide: CommunityStore,
    useClass: CharityCommunityStore,
  },
  {
    provide: ManageCommunityService,
    useFactory: createCommunityServiceFactory<CreateCharityRequest, UpdateCharityRequest>(),
  },
];
